import { AxiosResponse } from 'axios';
import _ from 'lodash';
import Logger from '../logger';
import { User } from '../types/user.types';
import { API_V1 } from './APIs/APIv1';

export interface UserResponse {
  count: number;
  users: User[];
}

function getParams(
  pageNumber?: number,
  pageSize?: number,
  sortAttribute?: string,
  sortDirection?: string,
  searchParam?: string,
  viewMode?: 'admin' | 'manager',
  showLicenses?: boolean
) {
  const params: any = {};

  if (pageNumber !== undefined) params.offset = pageNumber * (pageSize || 0);
  if (pageSize !== undefined) params.limit = pageSize;
  if (sortAttribute) params.sort = `${sortAttribute}:${sortDirection || 'asc'}`;
  if (searchParam) params.q = searchParam;
  if (viewMode) params.viewMode = viewMode;
  if (showLicenses) params.showLicenses = showLicenses;

  return params;
}

export const getAllUsers = async (
  pageNumber?: number,
  pageSize?: number,
  sortAttribute?: string,
  sortDirection?: string,
  searchParam?: string,
  viewMode?: 'admin' | 'manager',
  showLicenses?: boolean
): Promise<AxiosResponse<any, any>['data']> => {
  try {
    const response = await API_V1.get<UserResponse>('/users/legacy', {
      params: getParams(pageNumber, pageSize, sortAttribute, sortDirection, searchParam, viewMode, showLicenses),
    });
    return { data: response.data };
  } catch (e) {
    Logger.trace('Error while getting all users');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const editAccount = async (userId: string, accountData: Partial<User>) => {
  try {
    const response = await API_V1.post(`/users/legacy/${userId}`, accountData);
    return response.data;
  } catch (e) {
    Logger.trace('Error while updating user %s info by admin', userId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const resendInvitation = async (userId: string, tenantId?: string) => {
  try {
    const response = await API_V1.post(`/users/legacy/${userId}/resend-invitation`, {
      association: tenantId,
    });
    return response.data;
  } catch (e) {
    Logger.trace('Error while resending invitation for user %s', userId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const deleteAccount = async (userId: string, deleteData: boolean, toMoveUser?: string) => {
  try {
    const response = await API_V1.delete(`/users/legacy/${userId}`, {
      params: { deleteData, toMoveUserId: toMoveUser },
    });
    return response.data ?? { success: true };
  } catch (e) {
    Logger.trace('Error while deleting user %s info by admin', userId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const addAccount = async (
  emailAddr: string,
  firstName: string,
  surName: string,
  company: string,
  userRole: string,
  language: string,
  invitingUser: string,
  license?: string,
  functionInCompany?: string
) => {
  let companyValue: string | undefined = company;
  if (!company || company === '') {
    companyValue = undefined;
  }
  let firstnameValue: string | undefined = firstName;
  if (!firstName || firstName === '') {
    firstnameValue = undefined;
  }
  let lastnameValue: string | undefined = surName;
  if (!surName || surName === '') {
    lastnameValue = undefined;
  }
  let licenseValue: string | undefined = license;
  if (!license || license === '') {
    licenseValue = undefined;
  }
  try {
    const response = await API_V1.put(
      '/users/legacy',
      {
        firstname: firstnameValue,
        lastname: lastnameValue,
        email: emailAddr,
        role: userRole,
        association: companyValue,
        language,
        license: licenseValue,
        function: functionInCompany,
      },
      {
        params: {
          invitingUser,
        },
      }
    );
    return response.data;
  } catch (e) {
    Logger.trace('Error while creating new user by admin');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const getTenantUsers = async (userId: string): Promise<AxiosResponse<any, any>['data']> => {
  try {
    const response = await API_V1.get<UserResponse>(`/users/legacy/tenant-users/${userId}`);
    return { data: response.data };
  } catch (e) {
    Logger.trace('Error while getting all users');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const optOutUser = async (email: string): Promise<AxiosResponse<any, any>['data']> => {
  try {
    const response = await API_V1.get(`/users/legacy/optout/${email}`);
    return { data: response.data };
  } catch (e) {
    Logger.trace('Error while opting out user');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const getUserInfo = async () => {
  try {
    const response = await API_V1.get('/users/me');
    return response.data;
  } catch (e) {
    Logger.trace('Error while getting user info');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const changeUserInfo = async (input: any) => {
  try {
    const response = await API_V1.patch('/users/me', input);
    return response.data;
  } catch (e) {
    Logger.trace('Error while changing user info');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const resetPassword = async (password: string, token: string, userId: string) => {
  try {
    const response = await API_V1.post('/auth/reset-password', {
      password,
      token,
      userId,
    });
    return response.data;
  } catch (e) {
    Logger.trace('Error while reseting password');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const changePassword = async (input: any) => {
  try {
    const response = await API_V1.post('/users/me/password', input);
    return response.data;
  } catch (e) {
    Logger.trace('Error while changing user password');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const forgotPassword = async (input: { email: string }) => {
  try {
    const response = await API_V1.post('/auth/forgot-password', input);
    return response.data;
  } catch (e) {
    Logger.trace('Error while requesting new password');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};
